<script setup>
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import { PREFERRED_LANGUAGE_OPTIONS } from 'dashboard/components-next/Contacts/preferredLanguages';
import ContactPreferenceDefinitionsAPI from 'dashboard/api/contactPreferenceDefinitions';
import {
  buildPreferencesPayload,
  compactPreferenceList,
  preferenceDefinitionOptions,
  preferencesFromRows,
} from 'dashboard/helper/contactPreferences';
import { useCloseCircleState } from './useCloseCircleState.js';

const emit = defineEmits(['save', 'closed']);

const { t } = useI18n();
const closeCircle = useCloseCircleState();
const isOpen = ref(false);
const editingMember = ref(null);
const isSaving = ref(false);
const saveError = ref(false);
const importantDatesSectionRef = ref(null);
const importantDateInputRefs = ref([]);
const preferenceDefinitions = ref([]);
let dateRowId = 0;

const newImportantDate = (date = {}) => {
  dateRowId += 1;
  return {
    id: date.id ?? `form_date_${dateRowId}`,
    type: date.type ?? 'birthday',
    label: date.label ?? '',
    date: date.date ?? '',
  };
};

const emptyForm = () => ({
  firstName: '',
  lastName: '',
  relationship: 'spouse',
  phone: '',
  email: '',
  preferredContactChannel: '',
  preferredLanguage: '',
  canBeContactedDirectly: false,
  importantDates: [],
  preferences: {},
  notes: '',
});

const form = reactive(emptyForm());

const normalizedImportantDates = (initial = {}, member = null) => {
  const source = member?.importantDates ?? initial.importantDates;
  const importantDates = Array.isArray(source) ? source : [];
  const dates = importantDates.filter(date => date.date).map(newImportantDate);

  if (dates.length) return dates;
  if (member?.birthday || initial.birthday) {
    return [
      newImportantDate({
        type: 'birthday',
        date: member?.birthday ?? initial.birthday,
      }),
    ];
  }

  return [];
};

const reset = (initial = {}, member = null) => {
  const sourceContactPreferences =
    member?.contactPreferences ?? initial.contactPreferences ?? [];

  Object.assign(form, emptyForm(), {
    firstName: member?.firstName ?? initial.firstName ?? '',
    lastName: member?.lastName ?? initial.lastName ?? '',
    relationship: member?.relationship ?? initial.relationship ?? 'spouse',
    phone: member?.phone ?? initial.phone ?? '',
    email: member?.email ?? initial.email ?? '',
    preferredContactChannel:
      member?.preferredContactChannel ?? initial.preferredContactChannel ?? '',
    preferredLanguage:
      member?.preferredLanguage ?? initial.preferredLanguage ?? '',
    canBeContactedDirectly:
      member?.canBeContactedDirectly ?? initial.canBeContactedDirectly ?? false,
    importantDates: normalizedImportantDates(initial, member),
    preferences: preferencesFromRows(sourceContactPreferences),
    notes: member?.notes ?? initial.notes ?? '',
  });
  editingMember.value = member;
};

const availablePreferenceDefinitions = computed(() => {
  const existingValueKeys = new Set(
    Object.keys(form.preferences).filter(key =>
      form.preferences[key]?.toString().trim()
    )
  );

  return preferenceDefinitions.value.filter(
    definition => definition.active || existingValueKeys.has(definition.key)
  );
});

const preferenceFormRows = computed(() =>
  availablePreferenceDefinitions.value.map(definition => ({
    key: definition.key,
    label: definition.label,
    fieldType: definition.field_type,
    options: preferenceDefinitionOptions(definition),
    placeholder: ['list', 'list_with_custom'].includes(definition.field_type)
      ? t('CONTACTS_LAYOUT.CARD.PREFERENCES.FORM.LIST_PLACEHOLDER')
      : t('CONTACTS_LAYOUT.CARD.PREFERENCES.FORM.TEXT_PLACEHOLDER'),
  }))
);

const preferenceInputValue = preference => {
  const value = form.preferences[preference.key];
  if (['list', 'list_with_custom'].includes(preference.fieldType)) {
    return compactPreferenceList(value);
  }
  if (Array.isArray(value)) return value.join(', ');

  return value || '';
};

const setPreferenceValue = (preference, value) => {
  form.preferences[preference.key] = value;
};

const addImportantDate = () => {
  const date = newImportantDate();
  form.importantDates.push(date);
  return date;
};

const removeImportantDate = index => {
  form.importantDates.splice(index, 1);
};

const onImportantDateTypeChange = importantDate => {
  if (importantDate.type !== 'other') {
    importantDate.label = '';
  }
};

const setImportantDateInputRef = (el, index) => {
  if (el) {
    importantDateInputRefs.value[index] = el;
  }
};

const focusLastImportantDate = async () => {
  await nextTick();
  importantDatesSectionRef.value?.scrollIntoView({
    block: 'start',
    behavior: 'smooth',
  });

  const inputs = importantDateInputRefs.value;
  inputs[inputs.length - 1]?.focus();
};

const open = (initial = {}, member = null, options = {}) => {
  reset(initial, member);
  importantDateInputRefs.value = [];
  if (options.addImportantDate) {
    addImportantDate();
  }
  saveError.value = false;
  isOpen.value = true;
  if (options.addImportantDate) {
    focusLastImportantDate();
  }
};

const close = (reason = 'dismissed') => {
  if (isSaving.value) return;
  isOpen.value = false;
  emit('closed', { reason });
};

const save = () => {
  if (!form.firstName.trim() || !form.relationship) return;
  isSaving.value = true;
  saveError.value = false;
  emit('save', {
    memberId: editingMember.value?.id ?? null,
    values: {
      ...form,
      contactPreferences: buildPreferencesPayload(
        availablePreferenceDefinitions.value,
        form.preferences
      ),
      importantDates: form.importantDates.map(date => ({ ...date })),
    },
    onSuccess: () => {
      isSaving.value = false;
      close('saved');
    },
    onError: () => {
      isSaving.value = false;
      saveError.value = true;
    },
  });
};

const fetchPreferenceDefinitions = async () => {
  try {
    const response = await ContactPreferenceDefinitionsAPI.getActive();
    preferenceDefinitions.value = response.data || [];
  } catch (error) {
    preferenceDefinitions.value = [];
  }
};

onMounted(fetchPreferenceDefinitions);

defineExpose({ open, close });
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[80] flex items-end justify-center pointer-events-none transition-colors duration-200 ease-in-out"
      :class="
        isOpen
          ? 'pointer-events-auto bg-n-alpha-black1 backdrop-blur-[4px]'
          : 'bg-transparent'
      "
      @click.self="close"
    >
      <div
        class="flex h-[88vh] max-h-[88vh] w-full flex-col overflow-hidden rounded-t-[1.5rem] bg-n-solid-2 shadow-xl transition-transform duration-200 ease-in-out sm:max-w-xl sm:rounded-t-2xl"
        :class="isOpen ? 'translate-y-0' : 'translate-y-full'"
      >
        <div class="flex items-center gap-3 bg-n-solid-1 px-4 py-3">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            class="!rounded-full bg-n-alpha-black2 shrink-0"
            :disabled="isSaving"
            @click="close"
          />
          <h3 class="truncate text-base font-medium text-n-slate-12">
            {{
              editingMember
                ? t('CLOSE_CIRCLE.FORM.EDIT_TITLE')
                : t('CLOSE_CIRCLE.FORM.CREATE_TITLE')
            }}
          </h3>
        </div>

        <div class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <div class="flex flex-col gap-2 bg-n-alpha-1 py-2">
            <div
              ref="importantDatesSectionRef"
              class="flex flex-col gap-3 bg-n-solid-1 px-4 py-4"
            >
              <div class="grid grid-cols-2 gap-3">
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-10">
                    {{ t('CLOSE_CIRCLE.FORM.FIRST_NAME') }}
                    <span class="text-n-ruby-9">*</span>
                  </label>
                  <input
                    v-model="form.firstName"
                    type="text"
                    class="no-margin m-0 w-full rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                    :placeholder="t('CLOSE_CIRCLE.FORM.FIRST_NAME_PLACEHOLDER')"
                  />
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-10">
                    {{ t('CLOSE_CIRCLE.FORM.LAST_NAME') }}
                  </label>
                  <input
                    v-model="form.lastName"
                    type="text"
                    class="no-margin m-0 w-full rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                  />
                </div>
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-10">
                  {{ t('CLOSE_CIRCLE.FORM.RELATIONSHIP') }}
                  <span class="text-n-ruby-9">*</span>
                </label>
                <div class="relative">
                  <select
                    v-model="form.relationship"
                    class="block w-full m-0 h-9 appearance-none bg-none border-0 rounded-lg bg-n-alpha-1 px-3 py-2 pr-8 text-sm text-n-slate-12 focus:outline-none"
                  >
                    <option
                      v-for="relationship in closeCircle.relationships"
                      :key="relationship.value"
                      :value="relationship.value"
                    >
                      {{ t(relationship.labelKey) }}
                    </option>
                  </select>
                  <span
                    class="i-lucide-chevron-down size-3 text-n-slate-10 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
                  />
                </div>
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-10">
                  {{ t('CLOSE_CIRCLE.FORM.NOTES') }}
                </label>
                <textarea
                  v-model="form.notes"
                  rows="3"
                  class="m-0 w-full resize-none rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                  :placeholder="t('CLOSE_CIRCLE.FORM.NOTES_PLACEHOLDER')"
                />
              </div>
            </div>

            <div class="flex flex-col gap-3 bg-n-solid-1 px-4 py-4">
              <div class="flex items-center justify-between gap-3">
                <span class="text-sm font-semibold text-n-slate-12">
                  {{ t('CLOSE_CIRCLE.FORM.IMPORTANT_DATES') }}
                </span>
                <button
                  type="button"
                  class="flex items-center gap-1.5 rounded-full bg-n-alpha-black2 px-2.5 py-1 text-xs font-medium text-n-slate-11 transition-colors hover:text-n-slate-12"
                  @click="addImportantDate"
                >
                  <span class="i-lucide-plus size-3.5" />
                  {{ t('CLOSE_CIRCLE.FORM.ADD_IMPORTANT_DATE') }}
                </button>
              </div>

              <div
                v-for="(importantDate, index) in form.importantDates"
                :key="importantDate.id"
                class="grid gap-2 rounded-lg bg-n-alpha-1 p-3 sm:grid-cols-[minmax(0,1fr)_9rem_auto]"
              >
                <div class="grid gap-2 sm:grid-cols-2">
                  <div class="relative">
                    <select
                      v-model="importantDate.type"
                      :aria-label="t('CLOSE_CIRCLE.FORM.EVENT')"
                      class="block h-9 w-full m-0 appearance-none bg-none border-0 rounded-lg bg-n-solid-1 px-3 py-2 pr-8 text-sm text-n-slate-12 focus:outline-none"
                      @change="onImportantDateTypeChange(importantDate)"
                    >
                      <option
                        v-for="dateType in closeCircle.dateTypes"
                        :key="dateType.value"
                        :value="dateType.value"
                      >
                        {{ t(dateType.labelKey) }}
                      </option>
                    </select>
                    <span
                      class="i-lucide-chevron-down size-3 text-n-slate-10 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
                    />
                  </div>
                  <input
                    v-if="importantDate.type === 'other'"
                    v-model="importantDate.label"
                    type="text"
                    class="no-margin m-0 h-9 w-full rounded-lg bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                    :placeholder="
                      t('CLOSE_CIRCLE.FORM.IMPORTANT_DATE_LABEL_PLACEHOLDER')
                    "
                  />
                </div>

                <input
                  :ref="el => setImportantDateInputRef(el, index)"
                  v-model="importantDate.date"
                  type="date"
                  :aria-label="t('CLOSE_CIRCLE.FORM.IMPORTANT_DATE')"
                  class="no-margin m-0 h-9 w-full rounded-lg bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none"
                />

                <Button
                  variant="ghost"
                  color="ruby"
                  size="sm"
                  icon="i-lucide-trash-2"
                  class="!rounded-full bg-n-alpha-black2 justify-self-start sm:justify-self-end"
                  :title="t('CLOSE_CIRCLE.FORM.REMOVE_IMPORTANT_DATE')"
                  @click="removeImportantDate(index)"
                />
              </div>
            </div>

            <div class="flex flex-col gap-3 bg-n-solid-1 px-4 py-4">
              <span class="text-sm font-semibold text-n-slate-12">
                {{ t('CLOSE_CIRCLE.FORM.CONTACT_DETAILS') }}
              </span>

              <div class="grid grid-cols-2 gap-3">
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-10">
                    {{ t('CLOSE_CIRCLE.FORM.PHONE') }}
                  </label>
                  <input
                    v-model="form.phone"
                    type="tel"
                    class="no-margin m-0 rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                    :placeholder="t('CLOSE_CIRCLE.FORM.PHONE_PLACEHOLDER')"
                  />
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-10">
                    {{ t('CLOSE_CIRCLE.FORM.EMAIL') }}
                  </label>
                  <input
                    v-model="form.email"
                    type="email"
                    class="no-margin m-0 rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                    :placeholder="t('CLOSE_CIRCLE.FORM.EMAIL_PLACEHOLDER')"
                  />
                </div>
              </div>

              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-n-slate-10">
                  {{ t('CLOSE_CIRCLE.FORM.PREFERRED_LANGUAGE') }}
                </label>
                <div class="relative">
                  <select
                    v-model="form.preferredLanguage"
                    class="block w-full m-0 h-9 appearance-none bg-none border-0 rounded-lg bg-n-alpha-1 px-3 py-2 pr-8 text-sm text-n-slate-12 focus:outline-none"
                  >
                    <option value="">
                      {{
                        t('CLOSE_CIRCLE.FORM.PREFERRED_LANGUAGE_PLACEHOLDER')
                      }}
                    </option>
                    <option
                      v-for="language in PREFERRED_LANGUAGE_OPTIONS"
                      :key="language.value"
                      :value="language.value"
                    >
                      {{ language.label }}
                    </option>
                  </select>
                  <span
                    class="i-lucide-chevron-down size-3 text-n-slate-10 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
                  />
                </div>
              </div>

              <label class="flex items-center gap-2 text-sm text-n-slate-11">
                <input
                  v-model="form.canBeContactedDirectly"
                  type="checkbox"
                  class="m-0"
                />
                {{ t('CLOSE_CIRCLE.FORM.DIRECT_CONTACT_ALLOWED') }}
              </label>
            </div>

            <div
              v-if="preferenceFormRows.length"
              class="flex flex-col gap-3 bg-n-solid-1 px-4 py-4"
            >
              <span class="text-sm font-semibold text-n-slate-12">
                {{ t('CLOSE_CIRCLE.FORM.PREFERENCES') }}
              </span>

              <div class="flex flex-col gap-3">
                <div
                  v-for="preference in preferenceFormRows"
                  :key="preference.key"
                  class="flex flex-col gap-1"
                >
                  <label class="text-xs font-medium text-n-slate-10">
                    {{ preference.label }}
                  </label>
                  <TagMultiSelectComboBox
                    v-if="
                      ['list', 'list_with_custom'].includes(
                        preference.fieldType
                      )
                    "
                    :model-value="preferenceInputValue(preference)"
                    :options="preference.options"
                    :placeholder="preference.placeholder"
                    :allow-custom="preference.fieldType === 'list_with_custom'"
                    @update:model-value="
                      value => setPreferenceValue(preference, value)
                    "
                  />
                  <input
                    v-else
                    :value="preferenceInputValue(preference)"
                    type="text"
                    class="no-margin m-0 rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                    :placeholder="preference.placeholder"
                    @input="setPreferenceValue(preference, $event.target.value)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="flex flex-col gap-2 border-t border-n-strong bg-n-solid-1 px-4 pb-4 pt-3"
        >
          <p v-if="saveError" class="text-sm text-n-ruby-11">
            {{ t('CLOSE_CIRCLE.FORM.SAVE_ERROR') }}
          </p>
          <div class="flex gap-2">
            <Button
              variant="outline"
              color="slate"
              size="md"
              class="flex-1"
              :label="t('CLOSE_CIRCLE.FORM.CANCEL')"
              :disabled="isSaving"
              @click="close"
            />
            <Button
              variant="solid"
              color="blue"
              size="md"
              class="flex-1"
              :label="
                isSaving
                  ? t('CLOSE_CIRCLE.FORM.SAVING')
                  : t('CLOSE_CIRCLE.FORM.SAVE')
              "
              :disabled="
                isSaving || !form.firstName.trim() || !form.relationship
              "
              @click="save"
            />
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
